import React from "react";
import { Link } from "react-router-dom";
import {
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  EnvelopeIcon,
  Cog6ToothIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import MarketingLayout from "../components/MarketingLayout";
import SEO from "../components/SEO";

const steps = [
  {
    number: "01",
    icon: Cog6ToothIcon,
    title: "Set Up Your Club",
    description:
      "We create your club for you. You define skill categories with weighted scoring, and invite your coaching staff. Customize rating scales and evaluation criteria to match your program's needs.",
    details: [
      "Custom skill categories (skating, shooting, game sense, etc.)",
      "Configurable 1-5, 1-10, or custom rating scales",
      "Weighted scoring for different skill importance",
      "Role-based staff access (admins, coordinators, coaches, evaluators)",
    ],
    color: "sky",
  },
  {
    number: "02",
    icon: CalendarDaysIcon,
    title: "Set Up Evaluations & Sessions",
    description:
      "Create evaluation sessions for different age groups, teams, or skill levels. Set up check-in to track player attendance and manage multiple sessions simultaneously.",
    details: [
      "One evaluation with multiple sessions across age groups or skill levels",
      "Pre-assign players to sessions or use open check-in on the day",
      "Assign different evaluators to each session",
      "Run sessions simultaneously or on different days",
    ],
    color: "indigo",
  },
  {
    number: "03",
    icon: UserGroupIcon,
    title: "Import Your Roster",
    description:
      "Bring your players in from TeamSnap, SportsEngine, or a CSV file. Player profiles include contact info for parent report delivery.",
    details: [
      "Import from TeamSnap, SportsEngine, and other platforms",
      "CSV upload for any roster format",
      "Parent email addresses for report delivery",
      "Manage multiple age groups and divisions",
    ],
    color: "emerald",
  },
  {
    number: "04",
    icon: ClipboardDocumentCheckIcon,
    title: "Run Evaluations",
    description:
      "Have multiple coaches score players simultaneously from any device. The scoring interface is designed for speed — tap to rate, rated players fall to the bottom of the list.",
    details: [
      "Multiple evaluators scoring at the same time",
      "Mobile-first scoring interface for field use",
      "Add written comments and notes per player",
      "Session check-in to track player attendance",
    ],
    color: "amber",
  },
  {
    number: "05",
    icon: ChartBarIcon,
    title: "Review Results & Rankings",
    description:
      "Scores are aggregated automatically into per-player breakdowns with rankings and percentiles. Use the data to make fair, transparent team placement decisions.",
    details: [
      "Automatic score aggregation across evaluators",
      "Per-skill breakdowns and overall rankings",
      "Percentile comparisons across the roster",
      "Export data for team building and draft tools",
    ],
    color: "violet",
  },
  {
    number: "06",
    icon: EnvelopeIcon,
    title: "Share Reports with Parents",
    description:
      "With one click, email every parent a secure link to their child's personalized evaluation report. Each report shows scores, rankings, and evaluator feedback — without exposing other players' data.",
    details: [
      "One-click bulk email to all parents",
      "Secure, token-based report links (no login required)",
      "Individual scores with anonymous peer percentiles",
      "Professional report format with your club branding",
    ],
    color: "rose",
  },
];

const colorMap = {
  sky: {
    bg: "bg-sky-100",
    text: "text-sky-600",
    number: "text-sky-200",
    check: "text-sky-500",
    border: "border-sky-200",
  },
  emerald: {
    bg: "bg-emerald-100",
    text: "text-emerald-600",
    number: "text-emerald-200",
    check: "text-emerald-500",
    border: "border-emerald-200",
  },
  amber: {
    bg: "bg-amber-100",
    text: "text-amber-600",
    number: "text-amber-200",
    check: "text-amber-500",
    border: "border-amber-200",
  },
  violet: {
    bg: "bg-violet-100",
    text: "text-violet-600",
    number: "text-violet-200",
    check: "text-violet-500",
    border: "border-violet-200",
  },
  rose: {
    bg: "bg-rose-100",
    text: "text-rose-600",
    number: "text-rose-200",
    check: "text-rose-500",
    border: "border-rose-200",
  },
  indigo: {
    bg: "bg-indigo-100",
    text: "text-indigo-600",
    number: "text-indigo-200",
    check: "text-indigo-500",
    border: "border-indigo-200",
  },
};

export default function HowItWorksPage() {
  return (
    <MarketingLayout>
      <SEO
        title="How It Works"
        path="/how-it-works"
        description="Set up your club, organize evaluation sessions by age group, import players, run evaluations with your coaching staff, view results, and share reports with parents."
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-white to-sky-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight">
            How It Works
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            From setup to parent reports, Eval Ninja handles the entire
            evaluation workflow in a few simple steps.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {steps.map((step, index) => {
              const colors = colorMap[step.color];
              const isReversed = index % 2 === 1;

              return (
                <div
                  key={step.number}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
                >
                  {/* Text side */}
                  <div className={isReversed ? "lg:order-2" : ""}>
                    <div className="flex items-center gap-4 mb-4">
                      <div
                        className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center`}
                      >
                        <step.icon className={`w-6 h-6 ${colors.text}`} />
                      </div>
                      <span
                        className={`text-5xl font-bold ${colors.number} select-none`}
                      >
                        {step.number}
                      </span>
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h2>
                    <p className="text-lg text-gray-600 leading-relaxed mb-6">
                      {step.description}
                    </p>
                    <ul className="space-y-3">
                      {step.details.map((detail) => (
                        <li
                          key={detail}
                          className="flex items-start gap-3 text-gray-600"
                        >
                          <svg
                            className={`w-5 h-5 ${colors.check} mt-0.5 shrink-0`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Visual side */}
                  <div className={`${isReversed ? "lg:order-1" : ""} relative`}>
                    <div
                      className={`bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border ${colors.border}`}
                    >
                      <StepVisual step={step} colors={colors} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Roles section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Everyone Has a Role
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Eval Ninja gives each person the right tools and the right level
              of access.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                role: "Club Admin",
                description:
                  "Full control over evaluations, staff, skills, settings, and billing. Manages the entire club.",
                icon: "👑",
              },
              {
                role: "Coach / Evaluator",
                description:
                  "Scores players during evaluation sessions. Sees only the sessions they're assigned to. Can view results of players in their assigned sessions.",
                icon: "🏅",
              },
              {
                role: "Coordinator",
                description:
                  "Manages evaluation player check-in. Can add players if there are late sign ups.",
                icon: "📋",
              },
              {
                role: "Parent",
                description:
                  "Receives a secure email link to view their child's personalized evaluation report.",
                icon: "👨‍👩‍👧",
              },
            ].map((r) => (
              <div
                key={r.role}
                className="bg-white rounded-xl p-6 border border-gray-100 text-center"
              >
                <div className="text-3xl mb-3">{r.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {r.role}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {r.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-slate-700 to-slate-800 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Ready to Get Started?
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Set up your club in minutes and run your first evaluation today.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-3 text-base font-medium rounded-md text-slate-800 bg-white hover:bg-gray-100 transition-colors shadow-sm"
            >
              Contact Us To Get Started
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-white/30 text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

// Visual illustrations for each step
function StepVisual({ step, colors }) {
  switch (step.number) {
    case "01":
      return (
        <img
          src="/assets/skills_configuration.png"
          alt="Skill configuration screen"
          className="w-full rounded-lg shadow-sm"
        />
      );
    case "02":
      return (
        <img
          src="/assets/eval_sessions.png"
          alt="Evaluation set up screen"
          className="w-full rounded-lg shadow-sm"
        />
      );
    case "03":
      return (
        <img
          src="/assets/import_players.png"
          alt="Player import screen"
          className="w-full rounded-lg shadow-sm"
        />
      );
    case "04":
      return (
        <div
          className="relative flex items-center justify-center"
          style={{ gap: "10px" }}
        >
          <img
            src="/assets/phone_1.png"
            alt="Phone evaluator screen"
            className="w-full rounded-lg shadow-sm"
            style={{ maxHeight: "375px", width: "auto" }}
          />
          <img
            src="/assets/phone_2.png"
            alt="Phone evaluator screen 2"
            className="w-full rounded-lg shadow-sm"
            style={{ maxHeight: "375px", width: "auto" }}
          />
        </div>
      );
    case "05":
      return (
        <img
          src="/assets/score_card_screen.png"
          alt="Evaluation results screen"
          className="w-full rounded-lg shadow-sm"
        />
      );
    case "06":
      return (
        <div className="relative flex items-center justify-center">
          <img
            src="/assets/parent_report.png"
            alt="Parent report screen"
            className="w-full rounded-lg shadow-sm"
            style={{ maxHeight: "375px", width: "auto" }}
          />
        </div>
      );
    default:
      return null;
  }
}
